import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Clock, Calendar, CalendarDays, Star, FileText, Link, Info, Archive } from 'lucide-react';
import { FeatureGate } from '../components/subscription';
import { PRO_FEATURES } from '../hooks/useSubscription';
import Header from '../components/Header';
import Sidebar from '../components/layout/Sidebar';
import CockpitView from '../components/CockpitView';
import ProgressCard from '../components/ProgressCard';
import Section from '../components/Section';
import HabitSegmentControl from '../components/HabitSegmentControl';
import LinkItem, { getAssignments, getUserFrequency, getUserScheduledDay } from '../components/LinkItem';
import LinkModal from '../components/LinkModal';
import PolicyModal from '../components/PolicyModal';
import ConfirmModal from '../components/ConfirmModal';
import OwnerFilter from '../components/OwnerFilter';
import InvitationBanner from '../components/InvitationBanner';
import OneOnOnePage from './OneOnOnePage';
import DocumentsPage from './DocumentsPage';
import TasksPage from './TasksPage';
import SchedulePage from './SchedulePage';
import MeetingsPage from './MeetingsPage';
import EODPlanningPage from './EODPlanningPage';
import WeeklyReviewPage from './WeeklyReviewPage';
import SettingsPage from './SettingsPage';
import MobileNav from '../components/MobileNav';
import WeeklyReviewReminder from '../components/WeeklyReviewReminder';
import { useLinks } from '../hooks/useLinks';
import { usePolicies } from '../hooks/usePolicies';
import { useCheckoffs } from '../hooks/useCheckoffs';
import { useAuth } from '../hooks/useAuth';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import FeatureOnboarding from '../components/common/FeatureOnboarding';

// Check if a user (by UID) is assigned to an item
function isUserAssigned(item, userId) {
  const assignments = getAssignments(item);
  return !!assignments[userId];
}

const SIDEBAR_STORAGE_KEY = 'sidebar-collapsed';

export default function Dashboard() {
  const { user, isAdmin, userProfile } = useAuth();

  const [activeTab, setActiveTab] = useState('cockpit');
  const [ownerFilter, setOwnerFilter] = useState('mine');
  const [mobileSegment, setMobileSegment] = useState('daily');
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: null, item: null });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    return saved === 'true';
  });

  // Ref for TasksPage add handler
  const taskAddHandler = useRef(null);
  // Ref for TimeBlocksPage add handler
  const scheduleAddHandler = useRef(null);
  // Ref for DocumentsPage add handler
  const documentsAddHandler = useRef(null);

  const { links, archivedLinks, loading: linksLoading, addLink, updateLink, deleteLink, togglePin, archiveLink, unarchiveLink } = useLinks();
  const { policies, archivedPolicies, loading: policiesLoading, addPolicy, updatePolicy, deletePolicy, archivePolicy, unarchivePolicy } = usePolicies();
  const { isChecked, toggleCheck, cleanupOldCheckoffs } = useCheckoffs();

  // Keyboard shortcuts for navigation and actions
  const handleAddAction = useCallback(() => {
    if (activeTab === 'habits') {
      setShowLinkModal(true);
    } else if (activeTab === 'policies') {
      setShowPolicyModal(true);
    } else if ((activeTab === 'tasks' || activeTab === 'inbox') && taskAddHandler.current) {
      taskAddHandler.current();
    } else if (activeTab === 'schedule' && scheduleAddHandler.current) {
      scheduleAddHandler.current();
    } else if (activeTab === 'documents' && documentsAddHandler.current) {
      documentsAddHandler.current();
    }
  }, [activeTab]);

  useKeyboardShortcuts({
    'n': handleAddAction,                           // New item (context-aware)
    'd': () => setActiveTab('cockpit'),             // Dashboard
    'i': () => setActiveTab('inbox'),               // Inbox
    'h': () => setActiveTab('habits'),              // Habits
    't': () => setActiveTab('tasks'),               // Tasks
    's': () => setActiveTab('schedule'),            // Schedule
    'g': () => setActiveTab('documents'),           // Goals
    'p': () => setActiveTab('policies'),            // Policies
    '1': () => setActiveTab('oneOnOne'),            // 1:1s
    'r': () => setActiveTab('eod'),                 // Daily Review
    ',': () => setActiveTab('settings'),            // Settings
  });

  // Clean up old checkoffs on mount
  // TEMPORARILY DISABLED - investigating permission errors
  // useEffect(() => {
  //   cleanupOldCheckoffs();
  // }, []);

  // Handle OAuth callback redirects (e.g., Slack integration)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('slack')) {
      // Switch to settings tab to show the Slack connection result
      setActiveTab('settings');
    }
  }, []);

  // Determine which user (UID) we're viewing based on filter
  const viewingUserId = useMemo(() => {
    if (ownerFilter === 'mine') return user?.uid;
    if (ownerFilter === 'all') return null;
    return ownerFilter; // ownerFilter is now a UID
  }, [ownerFilter, user?.uid]);

  // Separate quick links from regular links
  const quickLinks = useMemo(() => links.filter((l) => l.isQuickLink), [links]);
  const regularLinks = useMemo(() => links.filter((l) => !l.isQuickLink), [links]);

  // Filter regular links based on owner filter
  const filteredLinks = useMemo(() => {
    if (ownerFilter === 'all') return regularLinks;
    const targetUserId = ownerFilter === 'mine' ? user?.uid : ownerFilter;
    return regularLinks.filter((link) => isUserAssigned(link, targetUserId));
  }, [regularLinks, ownerFilter, user?.uid]);

  // Separate links by frequency for the viewing user
  // Get today's day of week (0 = Sunday, 1 = Monday, etc.)
  const todayDayOfWeek = new Date().getDay();

  // Check if a weekly item is scheduled for today for a specific user
  const isScheduledForToday = (link, userName) => {
    const freq = getUserFrequency(link, userName);
    if (freq !== 'weekly') return false;
    const scheduledDay = getUserScheduledDay(link, userName);
    return scheduledDay === todayDayOfWeek;
  };

  const getLinksForFrequency = (frequency, excludePinned = true) => {
    return filteredLinks.filter((link) => {
      if (excludePinned && link.pinned) return false;

      if (ownerFilter === 'all') {
        const assignments = getAssignments(link);
        const frequencies = Object.values(assignments);
        const priorityOrder = ['daily', 'weekly', 'monthly'];
        const highestFreq = priorityOrder.find(f => frequencies.includes(f));
        return highestFreq === frequency;
      } else {
        const userFreq = getUserFrequency(link, viewingUserId);
        return userFreq === frequency;
      }
    });
  };

  const pinnedLinks = useMemo(() => filteredLinks.filter((l) => l.pinned), [filteredLinks]);

  // Daily links: true daily items + weekly items scheduled for today
  const dailyLinks = useMemo(() => {
    const trueDailyLinks = getLinksForFrequency('daily');
    const scheduledWeeklyLinks = filteredLinks.filter((link) => {
      if (link.pinned) return false;
      if (ownerFilter === 'all') return false; // Don't show scheduled items in "all" view
      return isScheduledForToday(link, viewingUserId);
    });
    return [...trueDailyLinks, ...scheduledWeeklyLinks];
  }, [filteredLinks, ownerFilter, viewingUserId, todayDayOfWeek]);

  // Weekly links: exclude items scheduled for today (they show in Daily)
  const weeklyLinks = useMemo(() => {
    return getLinksForFrequency('weekly').filter((link) => {
      if (ownerFilter === 'all') return true;
      return !isScheduledForToday(link, viewingUserId);
    });
  }, [filteredLinks, ownerFilter, viewingUserId, todayDayOfWeek]);

  const monthlyLinks = useMemo(() => getLinksForFrequency('monthly'), [filteredLinks, ownerFilter, viewingUserId]);

  // Today's habits for cockpit: daily habits + weekly habits scheduled for today (for current user only)
  const todayHabits = useMemo(() => {
    const userId = user?.uid;
    if (!userId) return [];

    // Get daily habits for current user
    const dailyHabits = regularLinks.filter((link) => {
      const userFreq = getUserFrequency(link, userId);
      return userFreq === 'daily';
    });

    // Get weekly habits scheduled for today for current user
    const scheduledWeeklyHabits = regularLinks.filter((link) => {
      const userFreq = getUserFrequency(link, userId);
      if (userFreq !== 'weekly') return false;
      const scheduledDay = getUserScheduledDay(link, userId);
      return scheduledDay === todayDayOfWeek;
    });

    // Combine and dedupe (in case of overlap)
    const combined = [...dailyHabits, ...scheduledWeeklyHabits];
    return combined.filter((link, index, self) =>
      index === self.findIndex(l => l.id === link.id)
    );
  }, [regularLinks, user?.uid, todayDayOfWeek]);

  // Calculate progress based on viewing user
  const progressUserId = viewingUserId || user?.uid;

  const getProgressCounts = (frequency) => {
    const userLinks = regularLinks.filter((link) => {
      const userFreq = getUserFrequency(link, progressUserId);
      return userFreq === frequency;
    });
    const checked = userLinks.filter((link) => isChecked(link.id, frequency)).length;
    return { total: userLinks.length, checked };
  };

  const dailyProgress = getProgressCounts('daily');
  const weeklyProgress = getProgressCounts('weekly');
  const monthlyProgress = getProgressCounts('monthly');

  // Policy categories
  const policyCategories = useMemo(
    () => [...new Set(policies.map((p) => p.category))].sort(),
    [policies]
  );

  const handleAdd = () => {
    if (activeTab === 'tasks' || activeTab === 'inbox') {
      // Trigger TasksPage add handler
      if (taskAddHandler.current) {
        taskAddHandler.current();
      }
    } else if (activeTab === 'schedule') {
      // Trigger TimeBlocksPage add handler
      if (scheduleAddHandler.current) {
        scheduleAddHandler.current();
      }
    } else if (activeTab === 'documents') {
      // Trigger DocumentsPage add handler
      if (documentsAddHandler.current) {
        documentsAddHandler.current();
      }
    } else if (activeTab === 'policies') {
      setEditingPolicy(null);
      setShowPolicyModal(true);
    } else {
      // Habits tab
      setEditingLink(null);
      setShowLinkModal(true);
    }
  };

  const handleEditLink = (link) => {
    setEditingLink(link);
    setShowLinkModal(true);
  };

  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy);
    setShowPolicyModal(true);
  };

  const handleSaveLink = async (data) => {
    if (editingLink) {
      await updateLink(editingLink.id, data);
    } else {
      await addLink(data);
    }
    setShowLinkModal(false);
    setEditingLink(null);
  };

  const handleSavePolicy = async (data) => {
    if (editingPolicy) {
      await updatePolicy(editingPolicy.id, data);
    } else {
      await addPolicy(data);
    }
    setShowPolicyModal(false);
    setEditingPolicy(null);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm.type === 'link') {
      deleteLink(deleteConfirm.item.id);
    } else {
      deletePolicy(deleteConfirm.item.id);
    }
    setDeleteConfirm({ show: false, type: null, item: null });
  };

  // Get the frequency for checkoff based on viewing context
  const getCheckoffFrequency = (link) => {
    return getUserFrequency(link, viewingUserId || user?.uid);
  };

  const loading = linksLoading || policiesLoading;

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent to-orange-hover animate-pulse" />
          <div className="text-text-tertiary text-sm">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  // Get task management setting from user profile
  const taskManagementEnabled = userProfile?.taskManagementEnabled ?? true;

  return (
    <div className="min-h-screen bg-surface-base">
      {/* Desktop Sidebar - hidden on mobile via CSS */}
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        taskManagementEnabled={taskManagementEnabled}
        isCollapsed={sidebarCollapsed}
        setIsCollapsed={setSidebarCollapsed}
      />

      {/* Mobile Header - visible on mobile only */}
      <div className="md:hidden">
        <Header activeTab={activeTab} setActiveTab={setActiveTab} onAdd={handleAdd} />
      </div>

      <InvitationBanner />

      {/* Main content area - offset by sidebar width on desktop */}
      <main className={`main-with-sidebar max-w-[1400px] px-3 md:px-6 py-4 md:py-5 ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Cockpit Dashboard Tab */}
        {activeTab === 'cockpit' && (
          <div className="animate-in">
            <WeeklyReviewReminder onNavigateToReview={() => setActiveTab('weeklyReview')} />
            <CockpitView
              habits={todayHabits}
              isChecked={isChecked}
              toggleCheck={toggleCheck}
              getCheckoffFrequency={getCheckoffFrequency}
              onNavigateToInbox={() => setActiveTab('inbox')}
            />
          </div>
        )}

        {/* Habits Tab */}
        {activeTab === 'habits' && (
          <FeatureOnboarding feature="habits">
          <div className="animate-in">
            {/* ========== MOBILE VIEW ========== */}
            <div className="md:hidden">
              {/* Owner Filter */}
              <div className="mb-3">
                <OwnerFilter
                  value={ownerFilter}
                  onChange={setOwnerFilter}
                />
              </div>

              {/* Segment Control */}
              <HabitSegmentControl
                selected={mobileSegment}
                onChange={setMobileSegment}
                counts={{
                  daily: dailyProgress,
                  weekly: weeklyProgress,
                  monthly: monthlyProgress,
                  links: quickLinks.length
                }}
              />

              {/* Pinned Section (shows in all segments) */}
              {pinnedLinks.length > 0 && (
                <Section
                  title="Pinned"
                  icon={Star}
                  iconColor="text-accent"
                  count={pinnedLinks.length}
                >
                  {pinnedLinks.map((link) => {
                    const freq = getCheckoffFrequency(link);
                    return (
                      <LinkItem
                        key={link.id}
                        item={link}
                        checked={freq ? isChecked(link.id, freq) : false}
                        onToggleCheck={freq ? () => toggleCheck(link.id, freq) : null}
                        onTogglePin={() => togglePin(link.id, link.pinned)}
                        onEdit={() => handleEditLink(link)}
                        onArchive={() => archiveLink(link.id)}
                        showFrequency={true}
                        viewingUserId={viewingUserId}
                        isAdmin={isAdmin}
                      />
                    );
                  })}
                </Section>
              )}

              {/* Daily Section */}
              {mobileSegment === 'daily' && (
                <div className="space-y-2">
                  {dailyLinks.map((link) => {
                    const actualFreq = getUserFrequency(link, viewingUserId || user?.uid);
                    return (
                      <LinkItem
                        key={link.id}
                        item={link}
                        checked={isChecked(link.id, actualFreq)}
                        onToggleCheck={() => toggleCheck(link.id, actualFreq)}
                        onTogglePin={() => togglePin(link.id, link.pinned)}
                        onEdit={() => handleEditLink(link)}
                        onArchive={() => archiveLink(link.id)}
                        showFrequency={false}
                        viewingUserId={viewingUserId}
                        isAdmin={isAdmin}
                      />
                    );
                  })}
                  {dailyLinks.length === 0 && (
                    <p className="text-text-tertiary text-sm italic py-4 text-center">
                      {pinnedLinks.some((l) => getUserFrequency(l, viewingUserId || user?.uid) === 'daily')
                        ? 'All daily items are pinned above'
                        : 'No daily habits assigned'}
                    </p>
                  )}
                </div>
              )}

              {/* Weekly Section */}
              {mobileSegment === 'weekly' && (
                <div className="space-y-2">
                  {weeklyLinks.map((link) => (
                    <LinkItem
                      key={link.id}
                      item={link}
                      checked={isChecked(link.id, 'weekly')}
                      onToggleCheck={() => toggleCheck(link.id, 'weekly')}
                      onTogglePin={() => togglePin(link.id, link.pinned)}
                      onEdit={() => handleEditLink(link)}
                      onArchive={() => archiveLink(link.id)}
                      showFrequency={false}
                      viewingUserId={viewingUserId}
                      isAdmin={isAdmin}
                    />
                  ))}
                  {weeklyLinks.length === 0 && (
                    <p className="text-text-tertiary text-sm italic py-4 text-center">
                      {pinnedLinks.some((l) => getUserFrequency(l, viewingUserId || user?.uid) === 'weekly')
                        ? 'All weekly items are pinned above'
                        : 'No weekly habits assigned'}
                    </p>
                  )}
                </div>
              )}

              {/* Monthly Section */}
              {mobileSegment === 'monthly' && (
                <div className="space-y-2">
                  {monthlyLinks.map((link) => (
                    <LinkItem
                      key={link.id}
                      item={link}
                      checked={isChecked(link.id, 'monthly')}
                      onToggleCheck={() => toggleCheck(link.id, 'monthly')}
                      onTogglePin={() => togglePin(link.id, link.pinned)}
                      onEdit={() => handleEditLink(link)}
                      onArchive={() => archiveLink(link.id)}
                      showFrequency={false}
                      viewingUserId={viewingUserId}
                      isAdmin={isAdmin}
                    />
                  ))}
                  {monthlyLinks.length === 0 && (
                    <p className="text-text-tertiary text-sm italic py-4 text-center">
                      {pinnedLinks.some((l) => getUserFrequency(l, viewingUserId || user?.uid) === 'monthly')
                        ? 'All monthly items are pinned above'
                        : 'No monthly habits assigned'}
                    </p>
                  )}
                </div>
              )}

              {/* Quick Links Section */}
              {mobileSegment === 'links' && (
                <div className="space-y-2">
                  {quickLinks.map((link) => (
                    <LinkItem
                      key={link.id}
                      item={link}
                      onTogglePin={() => togglePin(link.id, link.pinned)}
                      onEdit={() => handleEditLink(link)}
                      onArchive={() => archiveLink(link.id)}
                      showFrequency={false}
                      showPin={false}
                      isAdmin={isAdmin}
                    />
                  ))}
                  {quickLinks.length === 0 && (
                    <p className="text-text-tertiary text-sm italic py-4 text-center">
                      No quick links yet
                    </p>
                  )}
                </div>
              )}

              {/* Archived Section (mobile) */}
              {archivedLinks.length > 0 && (
                <Section
                  title="Archived"
                  icon={Archive}
                  iconColor="text-text-tertiary"
                  count={archivedLinks.length}
                  defaultOpen={false}
                >
                  {archivedLinks.map((link) => (
                    <LinkItem
                      key={link.id}
                      item={link}
                      onEdit={() => handleEditLink(link)}
                      onUnarchive={() => unarchiveLink(link.id)}
                      onDelete={() => setDeleteConfirm({ show: true, type: 'link', item: link })}
                      isArchived={true}
                      showFrequency={true}
                      showPin={false}
                      isAdmin={isAdmin}
                    />
                  ))}
                </Section>
              )}
            </div>

            {/* ========== DESKTOP VIEW ========== */}
            <div className="hidden md:block">
              {/* Progress Cards */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="animate-in delay-1 opacity-0" style={{ animationFillMode: 'forwards' }}>
                  <ProgressCard
                    icon={Clock}
                    label="Daily"
                    checked={dailyProgress.checked}
                    total={dailyProgress.total}
                    iconColor="text-accent"
                    progressColor="bg-accent"
                  />
                </div>
                <div className="animate-in delay-2 opacity-0" style={{ animationFillMode: 'forwards' }}>
                  <ProgressCard
                    icon={Calendar}
                    label="Weekly"
                    checked={weeklyProgress.checked}
                    total={weeklyProgress.total}
                    iconColor="text-weekly"
                    progressColor="bg-weekly"
                  />
                </div>
                <div className="animate-in delay-3 opacity-0" style={{ animationFillMode: 'forwards' }}>
                  <ProgressCard
                    icon={CalendarDays}
                    label="Monthly"
                    checked={monthlyProgress.checked}
                    total={monthlyProgress.total}
                    iconColor="text-monthly"
                    progressColor="bg-monthly"
                  />
                </div>
              </div>

              {/* Reset Info Banner */}
              <div className="flex bg-surface-card/50 rounded-lg p-2.5 mb-4 border border-border items-center gap-4 text-[10px] animate-in delay-4 opacity-0" style={{ animationFillMode: 'forwards' }}>
                <div className="flex items-center gap-1.5">
                  <Info size={12} className="text-text-tertiary" />
                  <span className="text-text-tertiary">Reset:</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                  <span className="text-text-secondary"><strong className="text-text-primary">Daily</strong> midnight</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-weekly" />
                  <span className="text-text-secondary"><strong className="text-text-primary">Weekly</strong> Mon 12AM</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-monthly" />
                  <span className="text-text-secondary"><strong className="text-text-primary">Monthly</strong> 1st</span>
                </div>
              </div>

              {/* Owner Filter */}
              <div className="mb-4 animate-in delay-5 opacity-0" style={{ animationFillMode: 'forwards' }}>
                <OwnerFilter
                  value={ownerFilter}
                  onChange={setOwnerFilter}
                />
              </div>

              {/* Pinned Section */}
              {pinnedLinks.length > 0 && (
                <div className="animate-in delay-6 opacity-0" style={{ animationFillMode: 'forwards' }}>
                  <Section
                    title="Pinned"
                    icon={Star}
                    iconColor="text-accent"
                    count={pinnedLinks.length}
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                      {pinnedLinks.map((link) => {
                        const freq = getCheckoffFrequency(link);
                        return (
                          <LinkItem
                            key={link.id}
                            item={link}
                            checked={freq ? isChecked(link.id, freq) : false}
                            onToggleCheck={freq ? () => toggleCheck(link.id, freq) : null}
                            onTogglePin={() => togglePin(link.id, link.pinned)}
                            onEdit={() => handleEditLink(link)}
                            onArchive={() => archiveLink(link.id)}
                            showFrequency={true}
                            viewingUserId={viewingUserId}
                            isAdmin={isAdmin}
                          />
                        );
                      })}
                    </div>
                  </Section>
                </div>
              )}

              {/* 2x2 Grid for Daily/Weekly/Monthly/Quick Links */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Daily - Top Left */}
                <Section
                  title="Daily Checks"
                  icon={Clock}
                  iconColor="text-accent"
                  count={dailyProgress.total}
                  checkedCount={dailyProgress.checked}
                >
                  {dailyLinks.map((link) => {
                    const actualFreq = getUserFrequency(link, viewingUserId || user?.uid);
                    return (
                      <LinkItem
                        key={link.id}
                        item={link}
                        checked={isChecked(link.id, actualFreq)}
                        onToggleCheck={() => toggleCheck(link.id, actualFreq)}
                        onTogglePin={() => togglePin(link.id, link.pinned)}
                        onEdit={() => handleEditLink(link)}
                        onArchive={() => archiveLink(link.id)}
                        showFrequency={false}
                        viewingUserId={viewingUserId}
                        isAdmin={isAdmin}
                      />
                    );
                  })}
                  {dailyLinks.length === 0 && (
                    <p className="text-text-tertiary text-sm italic py-2">
                      {pinnedLinks.some((l) => getUserFrequency(l, viewingUserId || user?.uid) === 'daily')
                        ? 'All daily items are pinned above'
                        : 'No daily links assigned'}
                    </p>
                  )}
                </Section>

                {/* Weekly - Top Right */}
                <Section
                  title="Weekly Checks"
                  icon={Calendar}
                  iconColor="text-weekly"
                  count={weeklyProgress.total}
                  checkedCount={weeklyProgress.checked}
                  defaultOpen={false}
                >
                  {weeklyLinks.map((link) => (
                    <LinkItem
                      key={link.id}
                      item={link}
                      checked={isChecked(link.id, 'weekly')}
                      onToggleCheck={() => toggleCheck(link.id, 'weekly')}
                      onTogglePin={() => togglePin(link.id, link.pinned)}
                      onEdit={() => handleEditLink(link)}
                      onArchive={() => archiveLink(link.id)}
                      showFrequency={false}
                      viewingUserId={viewingUserId}
                      isAdmin={isAdmin}
                    />
                  ))}
                  {weeklyLinks.length === 0 && (
                    <p className="text-text-tertiary text-sm italic py-2">
                      {pinnedLinks.some((l) => getUserFrequency(l, viewingUserId || user?.uid) === 'weekly')
                        ? 'All weekly items are pinned above'
                        : 'No weekly links assigned'}
                    </p>
                  )}
                </Section>

                {/* Monthly - Bottom Left */}
                <Section
                  title="Monthly Checks"
                  icon={CalendarDays}
                  iconColor="text-monthly"
                  count={monthlyProgress.total}
                  checkedCount={monthlyProgress.checked}
                  defaultOpen={false}
                >
                  {monthlyLinks.map((link) => (
                    <LinkItem
                      key={link.id}
                      item={link}
                      checked={isChecked(link.id, 'monthly')}
                      onToggleCheck={() => toggleCheck(link.id, 'monthly')}
                      onTogglePin={() => togglePin(link.id, link.pinned)}
                      onEdit={() => handleEditLink(link)}
                      onArchive={() => archiveLink(link.id)}
                      showFrequency={false}
                      viewingUserId={viewingUserId}
                      isAdmin={isAdmin}
                    />
                  ))}
                  {monthlyLinks.length === 0 && (
                    <p className="text-text-tertiary text-sm italic py-2">
                      {pinnedLinks.some((l) => getUserFrequency(l, viewingUserId || user?.uid) === 'monthly')
                        ? 'All monthly items are pinned above'
                        : 'No monthly links assigned'}
                    </p>
                  )}
                </Section>

                {/* Quick Links - Bottom Right */}
                <Section
                  title="Quick Links"
                  icon={Link}
                  iconColor="text-text-tertiary"
                  count={quickLinks.length}
                  defaultOpen={false}
                >
                  {quickLinks.map((link) => (
                    <LinkItem
                      key={link.id}
                      item={link}
                      onTogglePin={() => togglePin(link.id, link.pinned)}
                      onEdit={() => handleEditLink(link)}
                      onArchive={() => archiveLink(link.id)}
                      showFrequency={false}
                      showPin={false}
                      isAdmin={isAdmin}
                    />
                  ))}
                  {quickLinks.length === 0 && (
                    <p className="text-text-tertiary text-sm italic py-2">
                      No quick links yet
                    </p>
                  )}
                </Section>
              </div>

              {/* Archived Section */}
              {archivedLinks.length > 0 && (
                <Section
                  title="Archived"
                  icon={Archive}
                  iconColor="text-text-tertiary"
                  count={archivedLinks.length}
                  defaultOpen={false}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                    {archivedLinks.map((link) => (
                      <LinkItem
                        key={link.id}
                        item={link}
                        onEdit={() => handleEditLink(link)}
                        onUnarchive={() => unarchiveLink(link.id)}
                        onDelete={() => setDeleteConfirm({ show: true, type: 'link', item: link })}
                        isArchived={true}
                        showFrequency={true}
                        showPin={false}
                        isAdmin={isAdmin}
                      />
                    ))}
                  </div>
                </Section>
              )}
            </div>
          </div>
          </FeatureOnboarding>
        )}

        {/* Policies Tab */}
        {activeTab === 'policies' && (
          <div className="animate-in">
            {policyCategories.length === 0 && archivedPolicies.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-2xl bg-surface-card border border-border flex items-center justify-center mx-auto mb-4">
                  <FileText size={28} className="text-text-tertiary" />
                </div>
                <p className="text-text-secondary mb-2">No policies yet</p>
                <p className="text-text-tertiary text-sm">Click "Add Policy" to create one</p>
              </div>
            ) : (
              <>
                {policyCategories.map((category) => (
                  <Section
                    key={category}
                    title={category}
                    icon={FileText}
                    count={policies.filter((p) => p.category === category).length}
                  >
                    {policies
                      .filter((p) => p.category === category)
                      .map((policy) => (
                        <LinkItem
                          key={policy.id}
                          item={policy}
                          onEdit={() => handleEditPolicy(policy)}
                          onArchive={() => archivePolicy(policy.id)}
                          showFrequency={false}
                          showPin={false}
                          isAdmin={isAdmin}
                        />
                      ))}
                  </Section>
                ))}

                {/* Archived Policies */}
                {archivedPolicies.length > 0 && (
                  <Section
                    title="Archived"
                    icon={Archive}
                    iconColor="text-text-tertiary"
                    count={archivedPolicies.length}
                    defaultOpen={false}
                  >
                    {archivedPolicies.map((policy) => (
                      <LinkItem
                        key={policy.id}
                        item={policy}
                        onEdit={() => handleEditPolicy(policy)}
                        onUnarchive={() => unarchivePolicy(policy.id)}
                        onDelete={() => setDeleteConfirm({ show: true, type: 'policy', item: policy })}
                        isArchived={true}
                        showFrequency={false}
                        showPin={false}
                        isAdmin={isAdmin}
                      />
                    ))}
                  </Section>
                )}
              </>
            )}
          </div>
        )}

        {/* Inbox Tab - Direct access to inbox view */}
        {activeTab === 'inbox' && (
          <div className="animate-in">
            <TasksPage onAddTask={taskAddHandler} initialView="inbox" />
          </div>
        )}

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div className="animate-in">
            <TasksPage onAddTask={taskAddHandler} />
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <div className="animate-in h-[calc(100vh-4rem)]">
            <SchedulePage onAddBlock={scheduleAddHandler} />
          </div>
        )}

        {/* Meetings Tab */}
        {activeTab === 'meetings' && (
          <div className="animate-in h-[calc(100vh-4rem)]">
            <MeetingsPage />
          </div>
        )}

        {/* EOD Planning Tab */}
        {activeTab === 'eod' && (
          <div className="animate-in h-[calc(100vh-4rem)]">
            <EODPlanningPage />
          </div>
        )}

        {/* Weekly Review Tab */}
        {activeTab === 'weeklyReview' && (
          <div className="animate-in h-[calc(100vh-4rem)]">
            <FeatureGate feature={PRO_FEATURES.WEEKLY_REVIEW}>
              <WeeklyReviewPage />
            </FeatureGate>
          </div>
        )}

        {/* 1:1s Tab */}
        {activeTab === 'oneOnOne' && (
          <div className="animate-in">
            <FeatureGate feature={PRO_FEATURES.ONE_ON_ONES}>
              <OneOnOnePage />
            </FeatureGate>
          </div>
        )}

        {/* Documents Tab (Goals) */}
        {activeTab === 'documents' && (
          <div className="animate-in">
            <FeatureGate feature={PRO_FEATURES.GOALS}>
              <DocumentsPage onAddGoal={documentsAddHandler} />
            </FeatureGate>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="animate-in">
            <SettingsPage />
          </div>
        )}
      </main>

      {/* Modals */}
      <LinkModal
        isOpen={showLinkModal}
        onClose={() => {
          setShowLinkModal(false);
          setEditingLink(null);
        }}
        onSave={handleSaveLink}
        editItem={editingLink}
      />

      <PolicyModal
        isOpen={showPolicyModal}
        onClose={() => {
          setShowPolicyModal(false);
          setEditingPolicy(null);
        }}
        onSave={handleSavePolicy}
        editItem={editingPolicy}
      />

      <ConfirmModal
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, type: null, item: null })}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${deleteConfirm.type === 'link' ? 'Link' : 'Policy'}`}
        message={`Are you sure you want to delete "${deleteConfirm.item?.name}"? This action cannot be undone.`}
      />

      {/* Mobile Bottom Navigation */}
      <MobileNav
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onAdd={handleAdd}
        showAdd={activeTab === 'tasks' || activeTab === 'inbox' || activeTab === 'habits' || activeTab === 'policies' || activeTab === 'schedule' || activeTab === 'documents'}
      />
    </div>
  );
}
